import React from 'react';
import { useLocation, useParams } from 'react-router';
import { Link } from '../i18n/routing';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

import SEOHead from '../components/SEOHead';
import NotFoundPage from './NotFoundPage';
import { withUtm } from '../utils/outbound';
import LeadCaptureCard from '../components/leads/LeadCaptureCard';
import { trackCtaClick, trackSignUp } from '../services/analytics';
import { FEATURE_PAGES, getFeaturePage } from '../data/featurePages';
import { tr } from '../i18n/text';
import { splitLocale } from '../i18n/siteLocales';

const REGISTER_URL = 'https://app.trainzilla.in/register';
const card = { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' };
const startBtn = 'inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-white transition-transform hover:-translate-y-0.5';
const gradient = { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' };

/** /features/:slug — one shipped feature per page. Content: src/data/featurePages.ts. */
const FeaturePage: React.FC = () => {
  const { slug = '' } = useParams();
  const { pathname } = useLocation();
  const isEnglish = splitLocale(pathname).locale === 'en';
  const f = getFeaturePage(slug);
  if (!f || (f.englishOnly && !isEnglish)) return <NotFoundPage />;

  const url = `https://trainzilla.app/features/${f.slug}`;
  const registerHref = withUtm(REGISTER_URL, { campaign: `feature-${f.slug}`, medium: 'landing', content: f.slug });
  const onStartFree = (placement: string) => {
    trackCtaClick(`feature_${f.slug}_start_free`, { placement });
    trackSignUp(`feature_${f.slug}_lp`, { placement });
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: f.faq.map((x) => ({ '@type': 'Question', name: tr(x.q), acceptedAnswer: { '@type': 'Answer', text: tr(x.a) } })),
  };
  const others = FEATURE_PAGES.filter((o) => o.slug !== f.slug && (isEnglish || !o.englishOnly));

  return (
    <div className="min-h-screen pt-24 pb-20" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <SEOHead title={tr(f.title)} description={tr(f.description)} keywords={f.keywords} canonicalUrl={url} extraSchema={faqSchema} />

      <div className="container-responsive relative z-10 max-w-5xl mx-auto px-4">
        <nav className="text-sm mb-6" aria-label={tr("Breadcrumb")} style={{ color: 'var(--text-secondary)' }}>
          <Link to="/features" style={{ color: 'var(--accent-indigo)' }}>{tr("Features")}</Link> / {tr(f.name)}
        </nav>
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold mb-6" style={{ backgroundColor: 'rgba(99,102,241,0.12)', color: 'var(--accent-indigo)' }}>
            {tr(f.eyebrow)}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold mb-5 leading-tight" style={{ color: 'var(--text-primary)' }}>{tr(f.h1)}</h1>
          <p className="text-lg md:text-xl mb-6" style={{ color: 'var(--text-secondary)' }}>{tr(f.intro)}</p>
          <a href={registerHref} onClick={() => onStartFree('hero')} className={startBtn} style={gradient}>
            {tr("Start free, no card")} <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-sm mt-3" style={{ color: 'var(--text-secondary)' }}>{tr(f.plan)}</p>
        </div>

        <h2 className="text-2xl md:text-3xl font-bold mt-16 mb-6" style={{ color: 'var(--text-primary)' }}>{tr("How it works")}</h2>
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {f.steps.map((s, i) => (
            <li key={tr(s.title)} className="p-6 rounded-2xl" style={card}>
              <div className="text-sm font-bold mb-2" style={{ color: 'var(--accent-indigo)' }}>{tr("Step")} {i + 1}</div>
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{tr(s.title)}</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{tr(s.body)}</p>
            </li>
          ))}
        </ol>

        <h2 className="text-2xl md:text-3xl font-bold mt-16 mb-6" style={{ color: 'var(--text-primary)' }}>{tr("What’s included")}</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {f.details.map((d) => (
            <li key={d} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--accent-emerald)' }} />
              {tr(d)}
            </li>
          ))}
        </ul>

        <h2 className="text-2xl md:text-3xl font-bold mt-16 mb-6" style={{ color: 'var(--text-primary)' }}>{tr("Questions")}</h2>
        <div className="space-y-4">
          {f.faq.map((x) => (
            <div key={tr(x.q)} className="p-6 rounded-2xl" style={card}>
              <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{tr(x.q)}</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{tr(x.a)}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl" style={card}>
            <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{tr("Related")}</h2>
            <ul className="space-y-2 text-sm">
              {f.related.map((r) => (
                <li key={r.to}><Link to={r.to} style={{ color: 'var(--accent-indigo)' }}>{tr(r.label)}</Link></li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl" style={card}>
            <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{tr("More features")}</h2>
            <ul className="space-y-2 text-sm">
              {others.map((o) => (
                <li key={o.slug}><Link to={`/features/${o.slug}`} style={{ color: 'var(--accent-indigo)' }}>{tr(o.name)}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        <LeadCaptureCard variant="call" context={`feature-${f.slug}`} className="mt-16" />

        <div className="mt-8 p-8 rounded-2xl text-center" style={card}>
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{tr("Try it with your first client")}</h2>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>{tr("Free for 30 days, no card.")}</p>
          <a href={registerHref} onClick={() => onStartFree('footer')} className={startBtn} style={gradient}>
            {tr("Start free")} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

/** /features — hub linking every feature page. */
export const FeaturesHubPage: React.FC = () => {
  const isEnglish = splitLocale(useLocation().pathname).locale === 'en';
  return (
  <div className="min-h-screen pt-24 pb-20" style={{ backgroundColor: 'var(--bg-primary)' }}>
    <SEOHead
      title={tr("Trainzilla Features for Coaches & Gyms | TrainZilla")}
      description={isEnglish
        ? "Every Trainzilla feature in one place: PDF plan import, PR tracking, direct payments with no commission, refer a coach, weekly class bookings, public page templates, MCP server, AI Assistant, health reports, session packs, invoices, check-in kiosk and client community."
        : tr("Every Trainzilla feature in one place: MCP server, AI Assistant, health report reader, session packs, client invoices, gym check-in kiosk and client community.")}
      canonicalUrl="https://trainzilla.app/features"
    />
    <div className="container-responsive relative z-10 max-w-5xl mx-auto px-4">
      <h1 className="text-3xl md:text-5xl font-bold mb-5 leading-tight" style={{ color: 'var(--text-primary)' }}>{tr("Features")}</h1>
      <p className="text-lg mb-10 max-w-3xl" style={{ color: 'var(--text-secondary)' }}>
        {tr("What coaches and gyms use Trainzilla for, one page each. Every feature here is live today.")}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {FEATURE_PAGES.filter((f) => isEnglish || !f.englishOnly).map((f) => (
          <Link key={f.slug} to={`/features/${f.slug}`} className="p-6 rounded-2xl block transition-transform hover:-translate-y-0.5" style={card}>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--accent-indigo)' }}>{tr(f.eyebrow)}</div>
            <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{tr(f.name)}</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{tr(f.description)}</p>
          </Link>
        ))}
      </div>
    </div>
  </div>
  );
};

export default FeaturePage;
